import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import { reportsService } from '../services'
import { fmtAmount, fmtSaleDateTime } from '../components/SalesReport/formatters'
import ArticleCodeCell from '../components/SalesReport/ArticleCodeCell'
import {
  formatPromotionLabel,
  getPromotionTypeLabel,
  getPromotionTypeStyle,
} from '../utils/posPromotionFormatters'

const STATUS_COLORS = {
  COMPLETED: '#11809A',
  DELIVERED: '#11809A',
  PROCESSING: '#E08D61',
  IN_PROGRESS: '#E08D61',
  ACCEPTED: '#E08D61',
  PENDING: '#DF0000',
  CREATED: '#DF0000',
  CANCELLED: '#A5A5A5',
  REJECTED: '#A5A5A5',
  FAILED: '#A5A5A5',
}

function getStatusColor(status) {
  const key = String(status || '').toUpperCase()
  return STATUS_COLORS[key] || '#A5A5A5'
}

function isReturnOrderType(orderType) {
  return orderType === 'Return' || orderType === 'POS_Return'
}

function returnOrderTypeLabel(orderType) {
  if (orderType === 'POS_Return') return 'Return'
  return orderType || 'Return'
}

function formatDisplayDate(value) {
  if (!value) return 'N/A'
  return fmtSaleDateTime(value)
}

function LineItemCard({ item }) {
  const appliedPromotion = item.appliedPromotion || null
  const promoStyle = appliedPromotion
    ? getPromotionTypeStyle(appliedPromotion.type)
    : null
  const originalUnitPrice = Number(item.originalUnitPrice || 0)
  const unitPrice = Number(item.unitPrice || 0)
  const hasDiscount =
    originalUnitPrice > unitPrice + 0.009 ||
    (item.discountType && item.discountType !== 'none' && Number(item.discountValue) > 0)

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: '#F9FAFB',
        borderRadius: 1,
        border: '1px solid #E5E7EB',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar
            variant="rounded"
            src={item.imageThumbnail || undefined}
            alt={item.productName || 'Product'}
            sx={{ width: 48, height: 48, bgcolor: '#f3f4f6', fontSize: 14, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#263238', mb: 0.5 }}>
              {item.productName || 'N/A'}
            </Typography>
            {item.articleCode && (
              <Box sx={{ mb: 0.5 }}>
                <Typography component="span" variant="caption" sx={{ color: '#9CA3AF', mr: 0.5 }}>
                  Article Code:
                </Typography>
                <ArticleCodeCell articleCode={item.articleCode} />
              </Box>
            )}
            {appliedPromotion && promoStyle && (
              <Chip
                size="small"
                label={getPromotionTypeLabel(appliedPromotion.type)}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 500,
                  bgcolor: promoStyle.bg,
                  color: promoStyle.color,
                  mb: 0.5,
                }}
              />
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Qty: {item.quantity}
              </Typography>
              {hasDiscount && originalUnitPrice > unitPrice ? (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#9CA3AF', textDecoration: 'line-through' }}
                  >
                    {fmtAmount(originalUnitPrice)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#11809A', fontWeight: 500 }}>
                    {fmtAmount(unitPrice)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  {fmtAmount(unitPrice)} each
                </Typography>
              )}
            </Box>
            {hasDiscount && (
              <Typography variant="caption" sx={{ color: '#11809A', display: 'block', mt: 0.25 }}>
                {appliedPromotion
                  ? formatPromotionLabel(appliedPromotion)
                  : `Discount: ${
                      item.discountType === 'percentage'
                        ? `${item.discountValue}%`
                        : fmtAmount(item.discountValue)
                    }`}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', flexShrink: 0 }}>
          {fmtAmount(item.totalPrice)}
        </Typography>
      </Box>
    </Box>
  )
}

function ReturnHistoryCard({ entry }) {
  const isReturn = isReturnOrderType(entry.orderType)
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: isReturn ? '#FEF2F2' : '#FFF7ED',
        borderRadius: 1,
        border: `1px solid ${isReturn ? '#FECACA' : '#FED7AA'}`,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Chip
          size="small"
          label={returnOrderTypeLabel(entry.orderType)}
          sx={{
            bgcolor: isReturn ? '#EF4444' : '#F97316',
            color: '#fff',
            fontWeight: 600,
            fontSize: 11,
          }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#263238' }}>
          {fmtAmount(entry.amount)}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1 }}>
        #{entry.orderId} · {formatDisplayDate(entry.orderDate)} · {entry.orderStatus || 'N/A'}
      </Typography>
      {(entry.lineItems || []).map((li, idx) => (
        <Box
          key={`${entry.orderId}-${idx}`}
          sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}
        >
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {li.productName}
            {li.articleCode ? ` (${li.articleCode})` : ''} × {li.quantity}
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {fmtAmount(li.totalPrice)}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function PosSaleInvoiceDetailPage() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const backPath = location.state?.backPath || '/pos-store-promotions/report'

  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!orderId) return
      try {
        setLoading(true)
        setError(null)
        const data = await reportsService.getSalesInvoiceDetail(orderId)
        if (!cancelled) setDetail(data)
      } catch (err) {
        console.error('Error loading invoice detail:', err)
        if (!cancelled) {
          setError('Failed to load invoice details')
          setDetail(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [orderId])

  const order = detail?.order
  const billing = detail?.billing
  const lineItems = detail?.lineItems || []
  const returnHistory = detail?.returnHistory || []
  const displayDate = order?.saleAt || order?.orderDate
  const statusColor = getStatusColor(order?.orderStatus)

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }

  if (error || !order) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {error || 'Invoice not found'}
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>
          Back to Reports
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backPath)}
        sx={{ mb: 2, color: '#11809A' }}
      >
        Back to Reports
      </Button>

      <Card sx={{ mb: 2, border: '1px solid #E5E7EB' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#222' }}>
              Invoice #{order.orderId}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#7D838E' }}>
                Invoice No
              </Typography>
              <Chip
                size="small"
                label={order.orderStatus || 'Unknown'}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: `${statusColor}20`,
                  color: statusColor,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#222' }}>
              {fmtAmount(billing?.total ?? order.amount)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#7D838E', mt: 0.5, display: 'block' }}>
              {formatDisplayDate(displayDate)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, border: '1px solid rgba(206,215,219,0.48)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238', mb: 2 }}>
            Invoice Summary
          </Typography>

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Invoice Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
              {formatDisplayDate(displayDate)}
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Shop
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
              {order.organizationName || 'N/A'}
            </Typography>
          </Box>

          {order.paymentType && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Payment Method
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
                {order.paymentType}
              </Typography>
            </Box>
          )}

          {order.paymentStatus && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Payment Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
                {order.paymentStatus}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#777F95' }}>
              Subtotal
            </Typography>
            <Typography variant="body2" sx={{ color: '#747272', fontWeight: 500 }}>
              {fmtAmount(billing?.mrp_subtotal ?? billing?.subtotal)}
            </Typography>
          </Box>

          {billing?.showPromotion && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#777F95' }}>
                Promotion
              </Typography>
              <Typography variant="body2" sx={{ color: '#08AC62', fontWeight: 500 }}>
                -{fmtAmount(billing.promotion)}
              </Typography>
            </Box>
          )}

          {(billing?.showDiscount ?? billing?.showBreakdown) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#777F95' }}>
                Discount
              </Typography>
              <Typography variant="body2" sx={{ color: '#08AC62', fontWeight: 500 }}>
                -{fmtAmount(billing.discount)}
              </Typography>
            </Box>
          )}

          {Number(billing?.vat) > 0.009 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#777F95' }}>
                VAT
              </Typography>
              <Typography variant="body2" sx={{ color: '#747272', fontWeight: 500 }}>
                {fmtAmount(billing.vat)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#777F95' }}>
              Total Bill
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238' }}>
              {fmtAmount(billing?.total ?? order.amount)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, border: '1px solid rgba(206,215,219,0.48)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Inventory2OutlinedIcon sx={{ color: '#11809A' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
              Line Items ({lineItems.length})
            </Typography>
          </Box>

          {lineItems.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', py: 3 }}>
              No line items found
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {lineItems.map((item) => (
                <LineItemCard key={item.lineItemId} item={item} />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {returnHistory.length > 0 && (
        <Card sx={{ border: '1px solid rgba(206,215,219,0.48)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238', mb: 1.5 }}>
              Return & Exchange History
            </Typography>
            {returnHistory.map((entry) => (
              <ReturnHistoryCard key={entry.orderId} entry={entry} />
            ))}
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default PosSaleInvoiceDetailPage
